import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AddRounded,
  DeleteOutlineRounded,
  EditRounded,
  RefreshRounded,
  RouteRounded,
  VisibilityRounded,
} from "@mui/icons-material";
import axiosInstance from "../axiosInstance";
import {
  ADMIN_COLORS,
  AdminHeader,
  AdminPage,
  ErrorAlert,
  LoadingState,
  ProcessChip,
  SearchField,
  SectionCard,
  StatCard,
  dangerButtonSx,
  fieldSx,
  getProcessMeta,
  normalizeText,
  primaryButtonSx,
  secondaryButtonSx,
  tableHeaderSx,
} from "../components/admin/AdminUI";

const initialForm = { nombre: "", abreviatura: "", nivel: "", tipo: "Gestion" };
const PROCESS_TYPES = ["Gobierno", "Gestion", "Calidad"];
const LEVELS = [1, 2, 3, 4, 5];

export default function AdminProcesos() {
  const [procesos, setProcesos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [tipoFilter, setTipoFilter] = useState("");
  const [nivelFilter, setNivelFilter] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [mode, setMode] = useState("create");
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const loadProcesos = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axiosInstance.get("/admin/procesos");
      setProcesos(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.response?.data?.message || "No se pudieron cargar los procesos.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProcesos();
  }, []);

  const stats = useMemo(() => {
    return procesos.reduce((acc, p) => {
      acc.total += 1;
      acc[p.tipo] = (acc[p.tipo] || 0) + 1;
      return acc;
    }, { total: 0 });
  }, [procesos]);

  const filtered = useMemo(() => {
    const term = normalizeText(search);
    return procesos.filter((p) => {
      const matchSearch = !term || normalizeText(`${p.nombre} ${p.abreviatura} ${p.tipo}`).includes(term);
      const matchTipo = !tipoFilter || p.tipo === tipoFilter;
      const matchNivel = !nivelFilter || String(p.nivel) === String(nivelFilter);
      return matchSearch && matchTipo && matchNivel;
    });
  }, [procesos, search, tipoFilter, nivelFilter]);

  const openDialog = (nextMode, proceso = null) => {
    setMode(nextMode);
    setSelected(proceso);
    setForm(proceso ? {
      nombre: proceso.nombre || "",
      abreviatura: proceso.abreviatura || "",
      nivel: proceso.nivel || "",
      tipo: proceso.tipo || "Gestion",
    } : initialForm);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSelected(null);
  };

  const handleChange = (field) => (event) => setForm((prev) => ({ ...prev, [field]: event.target.value }));

  const submit = async () => {
    try {
      const payload = { ...form, nivel: Number(form.nivel) };
      if (mode === "create") {
        await axiosInstance.post("/admin/procesos/create", payload);
      } else {
        await axiosInstance.put(`/admin/procesos/update/${selected._id}`, payload);
      }
      setSnackbar({ open: true, message: "Proceso guardado correctamente.", severity: "success" });
      await loadProcesos();
      closeDialog();
    } catch (err) {
      setSnackbar({ open: true, message: err?.response?.data?.message || "No se pudo guardar el proceso.", severity: "error" });
    }
  };

  const confirmDelete = async () => {
    try {
      await axiosInstance.delete(`/admin/procesos/delete/${deleteTarget._id}`);
      setSnackbar({ open: true, message: "Proceso eliminado correctamente.", severity: "info" });
      await loadProcesos();
    } catch (err) {
      setSnackbar({ open: true, message: err?.response?.data?.message || "No se pudo eliminar el proceso.", severity: "error" });
    } finally {
      setDeleteTarget(null);
    }
  };

  if (loading) return <LoadingState text="Cargando procesos..." />;

  return (
    <AdminPage>
      <AdminHeader
        backTo="/admin-dashboard"
        eyebrow="Modelo de evaluación"
        title="Procesos de datos"
        subtitle="Administra el catálogo de procesos, su ámbito y el nivel al que pertenecen dentro del modelo de madurez."
        icon={<RouteRounded />}
        actions={
          <>
            <Button variant="outlined" startIcon={<RefreshRounded />} sx={secondaryButtonSx} onClick={loadProcesos}>Actualizar</Button>
            <Button variant="contained" startIcon={<AddRounded />} sx={primaryButtonSx} onClick={() => openDialog("create")}>Nuevo proceso</Button>
          </>
        }
      />

      <ErrorAlert error={error} />

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}><StatCard title="Total procesos" value={stats.total} icon={<RouteRounded />} color={ADMIN_COLORS.textStrong} bg="#F8FAFC" /></Grid>
        {PROCESS_TYPES.map((type) => {
          const meta = getProcessMeta(type);
          return <Grid item xs={12} sm={6} md={3} key={type}><StatCard title={meta.label} value={stats[type] || 0} icon={<RouteRounded />} color={meta.color} bg={meta.bg} /></Grid>;
        })}
      </Grid>

      <SectionCard
        title="Catálogo de procesos"
        subtitle="Usa filtros para revisar procesos por ámbito o nivel."
        action={
          <Stack direction={{ xs: "column", lg: "row" }} spacing={1} sx={{ width: { xs: "100%", lg: "auto" } }}>
            <SearchField value={search} onChange={setSearch} placeholder="Buscar proceso o abreviatura..." />
            <FormControl size="small" sx={{ minWidth: 170, ...fieldSx }}>
              <InputLabel>Ámbito</InputLabel>
              <Select label="Ámbito" value={tipoFilter} onChange={(e) => setTipoFilter(e.target.value)}>
                <MenuItem value="">Todos</MenuItem>
                {PROCESS_TYPES.map((type) => <MenuItem key={type} value={type}>{getProcessMeta(type).label}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150, ...fieldSx }}>
              <InputLabel>Nivel</InputLabel>
              <Select label="Nivel" value={nivelFilter} onChange={(e) => setNivelFilter(e.target.value)}>
                <MenuItem value="">Todos</MenuItem>
                {LEVELS.map((level) => <MenuItem key={level} value={level}>Nivel {level}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
        }
      >
        <TableContainer>
          <Table>
            <TableHead sx={tableHeaderSx}>
              <TableRow>
                <TableCell>Proceso</TableCell>
                <TableCell>Ámbito</TableCell>
                <TableCell>Nivel</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((proceso) => (
                <TableRow key={proceso._id} hover sx={{ "& td": { borderColor: ADMIN_COLORS.border } }}>
                  <TableCell>
                    <Typography sx={{ fontWeight: 900, color: ADMIN_COLORS.textStrong }}>{proceso.nombre}</Typography>
                    <Typography sx={{ color: ADMIN_COLORS.muted, fontSize: ".75rem" }}>{proceso.abreviatura || "Sin abreviatura"}</Typography>
                  </TableCell>
                  <TableCell><ProcessChip type={proceso.tipo} /></TableCell>
                  <TableCell><Typography sx={{ fontWeight: 900 }}>Nivel {proceso.nivel || "-"}</Typography></TableCell>
                  <TableCell align="right">
                    <Tooltip title="Ver"><IconButton onClick={() => openDialog("view", proceso)}><VisibilityRounded /></IconButton></Tooltip>
                    <Tooltip title="Editar"><IconButton onClick={() => openDialog("edit", proceso)}><EditRounded /></IconButton></Tooltip>
                    <Tooltip title="Eliminar"><IconButton onClick={() => setDeleteTarget(proceso)} sx={{ color: ADMIN_COLORS.danger }}><DeleteOutlineRounded /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {!filtered.length ? <Box sx={{ p: 3 }}><Alert severity="info">No hay procesos para los filtros seleccionados.</Alert></Box> : null}
      </SectionCard>

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: "24px" } }}>
        <DialogTitle>
          <Typography sx={{ fontWeight: 950, color: ADMIN_COLORS.textStrong }}>
            {mode === "create" ? "Nuevo proceso" : mode === "edit" ? "Editar proceso" : "Detalle de proceso"}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: ADMIN_COLORS.surfaceSoft }}>
          <Grid container spacing={1.5} sx={{ mt: .1 }}>
            <Grid item xs={12}><TextField fullWidth label="Nombre del proceso" value={form.nombre} onChange={handleChange("nombre")} disabled={mode === "view"} sx={fieldSx} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Abreviatura" value={form.abreviatura} onChange={handleChange("abreviatura")} disabled={mode === "view"} sx={fieldSx} /></Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={fieldSx} disabled={mode === "view"}>
                <InputLabel>Nivel</InputLabel>
                <Select label="Nivel" value={form.nivel} onChange={handleChange("nivel")}>
                  {LEVELS.map((level) => <MenuItem key={level} value={level}>Nivel {level}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth sx={fieldSx} disabled={mode === "view"}>
                <InputLabel>Ámbito</InputLabel>
                <Select label="Ámbito" value={form.tipo} onChange={handleChange("tipo")}>
                  {PROCESS_TYPES.map((type) => <MenuItem key={type} value={type}>{getProcessMeta(type).label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeDialog} sx={secondaryButtonSx}>Cerrar</Button>
          {mode !== "view" ? <Button variant="contained" onClick={submit} sx={primaryButtonSx}>Guardar</Button> : null}
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} PaperProps={{ sx: { borderRadius: "22px" } }}>
        <DialogTitle>Eliminar proceso</DialogTitle>
        <DialogContent><Typography>Se eliminará <strong>{deleteTarget?.nombre}</strong>. Revisa antes si tiene preguntas asociadas.</Typography></DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} sx={secondaryButtonSx}>Cancelar</Button>
          <Button onClick={confirmDelete} variant="outlined" sx={dangerButtonSx}>Eliminar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3600} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: "14px" }}>{snackbar.message}</Alert>
      </Snackbar>
    </AdminPage>
  );
}
